import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  inject
} from '@angular/core';
import {
  NotificationVM,
  NotificationType,
  enrichNotification,
  getNotificationAvatar,
  getStatusTag
} from '../models/notification.model';
import { UserCompany } from '../../models/user/user-companies-response-dto.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  styleUrls: ['./notification-dropdown.component.scss']
})
export class NotificationDropdownComponent implements OnChanges {

  @Input() notifications: NotificationVM[] = [];
  @Input() unreadCount = 0;
  @Input() isAdmin = false;
  @Input() isLoading = false;
  // The set of companies the current user has access to. Used to resolve `companyId` on each
  // notification into a human-readable client name shown below the message.
  @Input() companies: UserCompany[] = [];
  // The company currently managed on this tab. Pending-change rows that belong to a different
  // company get a "Switch to …" hint so the admin knows the click will prompt an org switch.
  @Input() currentCompanyId: number | null = null;

  @Output() markAsRead = new EventEmitter<{ note: NotificationVM; index: number }>();
  @Output() markAllAsRead = new EventEmitter<void>();
  @Output() deleteNotification = new EventEmitter<{ note: NotificationVM; index: number }>();
  @Output() viewLeave = new EventEmitter<NotificationVM>();
  @Output() viewAll = new EventEmitter<void>();
  @Output() openSettings = new EventEmitter<void>();

  private readonly eRef = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);

  isDropdownOpen = false;
  activeTab: 'all' | 'unread' = 'all';
  enrichedNotifications: NotificationVM[] = [];
  filteredNotifications: NotificationVM[] = [];

  // Max items to show in dropdown
  private readonly MAX_DROPDOWN_ITEMS = 8;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isDropdownOpen && !this.eRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notifications']) {
      this.enrichedNotifications = (this.notifications || []).map(n => enrichNotification(n));
      this.applyFilter();
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.activeTab = 'all';
      this.applyFilter();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  switchTab(tab: 'all' | 'unread'): void {
    this.activeTab = tab;
    this.applyFilter();
  }

  private applyFilter(): void {
    let filtered = [...this.enrichedNotifications];

    switch (this.activeTab) {
      case 'unread':
        filtered = filtered.filter(n => !n.isRead);
        break;
    }

    this.filteredNotifications = filtered.slice(0, this.MAX_DROPDOWN_ITEMS);
  }

  getAvatarConfig(note: NotificationVM) {
    return getNotificationAvatar(note.type || NotificationType.System);
  }

  getStatusTag(note: NotificationVM) {
    return getStatusTag(note.type || NotificationType.System);
  }

  /** Resolve the notification's owning company to a display name. Empty when unknown. */
  companyName(id?: number): string {
    if (!id) return '';
    return this.companies?.find(c => c.id === id)?.name ?? '';
  }

  /** True when the notification's company differs from the one currently managed on this tab. */
  isCrossCompany(note: NotificationVM): boolean {
    return !!note.companyId
        && !!this.currentCompanyId
        && note.companyId !== this.currentCompanyId;
  }

  highlightMessage(message: string): SafeHtml {
    if (!message) return '';

    let html = message;

    // Bold names at the start
    html = html.replace(
      /^([A-Z][a-z]+\s+[A-Z][a-z]+)/,
      '<strong>$1</strong>'
    );

    // Bold "Leave for Name"
    html = html.replace(
      /Leave for\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/,
      'Leave for <strong>$1</strong>'
    );

    // Bold "from Name"
    html = html.replace(
      /from\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/,
      'from <strong>$1</strong>'
    );

    // Highlight dates in parentheses
    html = html.replace(
      /\(([^)]+\d{4}[^)]*)\)/g,
      '(<span class="highlight">$1</span>)'
    );

    // Highlight "from Date to Date"
    html = html.replace(
      /from\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)\s+to\s+(\w+\s+\d{1,2}(?:\s+\d{4})?)/,
      'from <span class="highlight">$1</span> to <span class="highlight">$2</span>'
    );

    // Highlight quoted note titles
    html = html.replace(
      /the note '([^']+)'/g,
      'the note \'<span class="highlight">$1</span>\''
    );

    // Style rejection reason
    html = html.replace(
      /Reason:\s*(.+)$/,
      '<em class="notif-reason">Reason: $1</em>'
    );

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onNotificationClick(note: NotificationVM, index: number): void {
    this.markAsRead.emit({ note, index });
    this.closeDropdown();
  }

  onMarkAllRead(): void {
    this.markAllAsRead.emit();
  }

  onDismiss(note: NotificationVM, index: number): void {
    this.deleteNotification.emit({ note, index });
  }

  onViewLeave(note: NotificationVM): void {
    this.viewLeave.emit(note);
    this.closeDropdown();
  }

  onViewAll(): void {
    this.viewAll.emit();
    this.closeDropdown();
  }

  onOpenSettings(): void {
    this.openSettings.emit();
  }

  trackByNotifId(index: number, item: NotificationVM): number {
    return item.id;
  }
}
